const ROOT_KEYS = new Set(['schemaVersion', 'defaultProfileId', 'profiles']);
const PROFILE_KEYS = new Set([
  'id',
  'label',
  'sourceType',
  'sourceStatus',
  'paymentScope',
  'totalRaisedCents',
  'donationCount',
  'averageDonationCents',
  'medianDonationCents',
  'donationHistogram',
  'notes',
]);
const HISTOGRAM_KEYS = new Set(['amountCents', 'count']);
const FORBIDDEN_KEY_PATTERN =
  /(name|email|student|business|charge|note|address|phone|private)/i;

function fail(path, message) {
  throw new Error(`Invalid fundraiser profile registry at ${path}: ${message}`);
}

function record(value, path) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    fail(path, 'expected an object');
  }
  return value;
}

function array(value, path) {
  if (!Array.isArray(value)) fail(path, 'expected an array');
  return value;
}

function string(value, path) {
  if (typeof value !== 'string' || value.trim() === '') {
    fail(path, 'expected a non-empty string');
  }
  return value;
}

function positiveInteger(value, path) {
  if (!Number.isSafeInteger(value) || value <= 0) {
    fail(path, 'expected a positive safe integer');
  }
  return value;
}

function exactKeys(value, allowedKeys, path) {
  for (const key of Object.keys(value)) {
    if (!allowedKeys.has(key)) {
      const detail = FORBIDDEN_KEY_PATTERN.test(key)
        ? 'contains a potentially identifying or unapproved field'
        : 'contains an unsupported field';
      fail(`${path}.${key}`, detail);
    }
  }
}

export function validateFundraiserProfileRegistry(value) {
  const root = record(value, '$');
  exactKeys(root, ROOT_KEYS, '$');
  if (root.schemaVersion !== 1) {
    fail('$.schemaVersion', 'unsupported schema version');
  }

  const defaultProfileId = string(root.defaultProfileId, '$.defaultProfileId');
  const profiles = array(root.profiles, '$.profiles');
  if (profiles.length === 0) fail('$.profiles', 'must not be empty');

  const profileIds = new Set();
  profiles.forEach((rawProfile, profileIndex) => {
    const path = `$.profiles[${profileIndex}]`;
    const profile = record(rawProfile, path);
    exactKeys(profile, PROFILE_KEYS, path);

    const id = string(profile.id, `${path}.id`);
    if (profileIds.has(id)) fail(`${path}.id`, `duplicate id "${id}"`);
    profileIds.add(id);
    string(profile.label, `${path}.label`);
    if (profile.sourceType !== 'anonymized-real-fundraiser') {
      fail(`${path}.sourceType`, 'must be anonymized-real-fundraiser');
    }
    if (profile.sourceStatus !== 'single-observed-fundraiser') {
      fail(`${path}.sourceStatus`, 'must be single-observed-fundraiser');
    }
    if (profile.paymentScope !== 'card') {
      fail(`${path}.paymentScope`, 'must be card');
    }

    const totalRaisedCents = positiveInteger(
      profile.totalRaisedCents,
      `${path}.totalRaisedCents`,
    );
    const donationCount = positiveInteger(
      profile.donationCount,
      `${path}.donationCount`,
    );
    const averageDonationCents = positiveInteger(
      profile.averageDonationCents,
      `${path}.averageDonationCents`,
    );
    const medianDonationCents = positiveInteger(
      profile.medianDonationCents,
      `${path}.medianDonationCents`,
    );
    string(profile.notes, `${path}.notes`);

    const histogram = array(profile.donationHistogram, `${path}.donationHistogram`);
    if (histogram.length === 0) {
      fail(`${path}.donationHistogram`, 'must not be empty');
    }

    let previousAmountCents = 0;
    let histogramCount = 0;
    let histogramTotalCents = 0;
    const expandedAmounts = [];
    histogram.forEach((rawBin, binIndex) => {
      const binPath = `${path}.donationHistogram[${binIndex}]`;
      const bin = record(rawBin, binPath);
      exactKeys(bin, HISTOGRAM_KEYS, binPath);
      const amountCents = positiveInteger(bin.amountCents, `${binPath}.amountCents`);
      const count = positiveInteger(bin.count, `${binPath}.count`);
      if (amountCents <= previousAmountCents) {
        fail(`${binPath}.amountCents`, 'amounts must be strictly increasing');
      }
      previousAmountCents = amountCents;
      histogramCount += count;
      histogramTotalCents += amountCents * count;
      for (let index = 0; index < count; index += 1) {
        expandedAmounts.push(amountCents);
      }
    });

    if (histogramCount !== donationCount) {
      fail(`${path}.donationHistogram`, 'counts must equal donationCount');
    }
    if (histogramTotalCents !== totalRaisedCents) {
      fail(`${path}.donationHistogram`, 'weighted amounts must equal totalRaisedCents');
    }
    if (Math.round(totalRaisedCents / donationCount) !== averageDonationCents) {
      fail(`${path}.averageDonationCents`, 'does not match the rounded profile average');
    }
    const middle = Math.floor(expandedAmounts.length / 2);
    const median =
      expandedAmounts.length % 2 === 1
        ? expandedAmounts[middle]
        : Math.round((expandedAmounts[middle - 1] + expandedAmounts[middle]) / 2);
    if (median !== medianDonationCents) {
      fail(`${path}.medianDonationCents`, 'does not match the profile histogram');
    }
  });

  if (!profileIds.has(defaultProfileId)) {
    fail('$.defaultProfileId', 'must reference a known profile');
  }

  return value;
}
